// Cached measurement element for DOM-based measurement
let measureElement: HTMLSpanElement | null = null;

function getMeasureElement(): HTMLSpanElement {
  if (!measureElement) {
    measureElement = document.createElement("span");
    measureElement.className = "proxima-nova"; // Use the app's font class
    measureElement.style.cssText = `
      position: absolute;
      visibility: hidden;
      white-space: nowrap;
      pointer-events: none;
      font-size: 16px;
    `;
    document.body.appendChild(measureElement);
  }
  return measureElement;
}

// Font presets matching our design system
export const FONT_PRESETS = {
  regular: "16px proxima-nova, sans-serif", // Default page font size
  regular14: "14px proxima-nova, sans-serif",
  mono: "13px ui-monospace, SFMono-Regular, 'SF Mono', Menlo, Consolas, monospace",
  small: "12px proxima-nova, sans-serif",
  label: "14px proxima-nova, sans-serif",
} as const;

export type FontPreset = keyof typeof FONT_PRESETS;

/**
 * Measure the width of a single text string using DOM measurement
 * This properly handles web fonts like proxima-nova
 * @param text - The text to measure
 * @param fontOrPreset - Either a FontPreset key or a custom CSS font string
 * @returns The width in pixels
 */
export function measureText(text: string, fontOrPreset: string | FontPreset = "regular"): number {
  const el = getMeasureElement();

  // Only set font-family for mono, otherwise inherit from proxima-nova class
  if (fontOrPreset === "mono") {
    el.style.fontFamily = "ui-monospace, SFMono-Regular, 'SF Mono', Menlo, Consolas, monospace";
    el.style.fontSize = "13px";
  } else {
    el.style.fontFamily = ""; // Inherit from class
    el.style.fontSize = "16px"; // Match default page font size
  }

  el.textContent = text;
  const width = el.getBoundingClientRect().width;

  return width;
}

/**
 * Find the maximum width from an array of text strings
 * @param texts - Array of texts to measure
 * @param fontOrPreset - Either a FontPreset key or a custom CSS font string
 * @returns The maximum width in pixels
 */
export function measureTextMax(texts: string[], fontOrPreset: string | FontPreset = "regular"): number {
  if (texts.length === 0) return 0;
  const el = getMeasureElement();
  const font = FONT_PRESETS[fontOrPreset as FontPreset] || fontOrPreset;
  el.style.font = font;
  return Math.max(...texts.map((t) => {
    el.textContent = t;
    return el.getBoundingClientRect().width;
  }));
}

/**
 * Measure the width of multiple text strings
 * @param texts - Array of texts to measure
 * @param fontOrPreset - Either a FontPreset key or a custom CSS font string
 * @returns Array of widths in pixels
 */
export function measureTexts(texts: string[], fontOrPreset: string | FontPreset = "regular"): number[] {
  const el = getMeasureElement();
  const font = FONT_PRESETS[fontOrPreset as FontPreset] || fontOrPreset;
  el.style.font = font;
  return texts.map((t) => {
    el.textContent = t;
    return el.getBoundingClientRect().width;
  });
}
